import { useMemo, useState } from "react";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import {
  CalendarPlus,
  CalendarWeek,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ExclamationTriangle,
  Search,
} from "react-bootstrap-icons";
import { PuffLoader } from "react-spinners";
import { cn } from "@utils";
import SubscribeSection from "./SubscribeSection";

// Past this many overlaid calendars each adds a discovery + fetch round-trip, so we nudge the user.
const HEAVY_SELECTION = 8;

// One "Calendars" flow with two steps: pick whose calendars to overlay, and add your own calendar to
// an external app. The people list can be long (100+ in a merchant), so it gets its own bounded
// scroll and "Add my calendar to another app" is a PINNED entry that opens its own step — never
// buried at the bottom of the list.
const CalendarsMenu = ({
  me,
  users,
  selectedIds,
  onToggle,
  onClear,
  sourceStatus = {},
  colorByUser = {},
  showTeam = true,
}) => {
  const [query, setQuery] = useState("");
  const [showSubscribe, setShowSubscribe] = useState(false);

  const colleagues = useMemo(
    () =>
      users
        .filter((user) => user.id !== me?.id)
        .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "")),
    [users, me?.id],
  );

  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return colleagues;
    return colleagues.filter((user) =>
      `${user.name ?? ""} ${user.email ?? ""}`.toLowerCase().includes(term),
    );
  }, [colleagues, query]);

  const selectedCount = colleagues.filter((user) => selectedIds.includes(user.id)).length;

  // Subscribe-only when the team overlay is disabled, or when the user has stepped into it.
  const subscribeView = !showTeam || showSubscribe;

  return (
    <Popover className="relative">
      <PopoverButton className="inline-flex items-center gap-1.5 rounded-md border border-zinc-200 bg-white px-3 py-1.5 text-sm font-medium text-zinc-700 shadow-sm transition-colors hover:bg-zinc-50 focus:outline-none">
        <CalendarWeek className="size-4 text-zinc-500" />
        Calendars
        {selectedCount > 0 && (
          <span className="ml-0.5 rounded-full bg-primary-100 px-1.5 text-xs font-semibold text-primary-700">
            {selectedCount}
          </span>
        )}
        <ChevronDown className="size-3.5 text-zinc-500" />
      </PopoverButton>

      <PopoverPanel className="absolute right-0 z-30 mt-2 flex max-h-[34rem] w-96 origin-top-right flex-col rounded-md bg-white p-1 shadow-lg ring-1 ring-black/5 focus:outline-none">
        {subscribeView ? (
          <div className="flex min-h-0 flex-1 flex-col">
            {showTeam && (
              <button
                type="button"
                onClick={() => setShowSubscribe(false)}
                className="flex shrink-0 items-center gap-1.5 rounded px-2 py-1.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50"
              >
                <ChevronLeft className="size-4 text-zinc-500" />
                Back
              </button>
            )}
            <div className="min-h-0 flex-1 overflow-y-auto">
              <SubscribeSection />
            </div>
          </div>
        ) : (
          <div className="flex min-h-0 flex-1 flex-col">
            <div className="shrink-0 px-2 pb-1 pt-1">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-medium text-zinc-900">Show colleagues&rsquo; calendars</p>
                {selectedCount > 0 && (
                  <button
                    type="button"
                    onClick={onClear}
                    className="shrink-0 text-xs font-medium text-primary-600 hover:text-primary-700"
                  >
                    Only me
                  </button>
                )}
              </div>
              <p className="mt-0.5 text-xs text-zinc-500">
                Overlay a colleague&rsquo;s calendar on top of your own.
              </p>
            </div>

            <div className="relative shrink-0 p-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-zinc-400" />
              <input
                type="text"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search people…"
                className="w-full rounded border border-zinc-200 py-1.5 pl-8 pr-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto">
              {!query.trim() && (
                <Row
                  label={me?.name ? `${me.name} (you)` : "My calendar"}
                  color={colorByUser[me?.id]}
                  checked
                  disabled
                />
              )}
              {filtered.length === 0 ? (
                <p className="px-2 py-3 text-center text-xs text-zinc-500">
                  {query.trim() ? "No people match" : "No colleagues to show"}
                </p>
              ) : (
                filtered.map((user) => {
                  const status = sourceStatus[user.id] ?? {};
                  return (
                    <Row
                      key={user.id}
                      label={user.name || user.email || "Unknown user"}
                      sublabel={user.name ? user.email : null}
                      color={colorByUser[user.id]}
                      checked={selectedIds.includes(user.id)}
                      onChange={() => onToggle(user.id)}
                      loading={status.isLoading}
                      error={status.isError}
                    />
                  );
                })
              )}
              {selectedCount > HEAVY_SELECTION && (
                <p className="px-2 py-1.5 text-[11px] text-zinc-400">
                  Showing {selectedCount} calendars — loading may be slower.
                </p>
              )}
            </div>

            {/* Pinned so it stays visible however long the people list is. */}
            <button
              type="button"
              onClick={() => setShowSubscribe(true)}
              className="flex shrink-0 items-center gap-2.5 rounded-b border-t border-zinc-200 bg-zinc-50 px-2 py-2.5 text-left hover:bg-zinc-100"
            >
              <CalendarPlus className="size-4 shrink-0 text-zinc-500" />
              <span className="flex-1 text-sm font-medium text-zinc-900">
                Add my calendar to another app
              </span>
              <ChevronRight className="size-4 shrink-0 text-zinc-400" />
            </button>
          </div>
        )}
      </PopoverPanel>
    </Popover>
  );
};

const Row = ({ label, sublabel, color, checked, disabled, onChange, loading, error }) => (
  <label
    className={cn(
      "flex items-center gap-2.5 rounded px-2 py-1.5",
      disabled ? "cursor-default" : "cursor-pointer hover:bg-zinc-50",
    )}
  >
    <span className="relative grid size-4 shrink-0 grid-cols-1">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={onChange}
        className={cn(
          "peer col-start-1 row-start-1 appearance-none rounded border bg-white disabled:opacity-50",
          color?.outlineClass ?? "border-zinc-300",
          color?.checkedBgClass ?? "checked:bg-primary",
        )}
      />
      <svg
        className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white opacity-0 peer-checked:opacity-100"
        viewBox="0 0 14 14"
        fill="none"
      >
        <path d="M3 8L6 11L11 3.5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
    <span className="min-w-0 flex-1">
      <span className="block truncate text-sm text-zinc-900">{label}</span>
      {sublabel && <span className="block truncate text-xs text-zinc-500">{sublabel}</span>}
    </span>
    {loading && <PuffLoader color="#F95816" size={14} />}
    {error && !loading && (
      <span title="Couldn't load this calendar">
        <ExclamationTriangle className="size-3.5 text-yellow-500" />
      </span>
    )}
  </label>
);

export default CalendarsMenu;
